import React, { useState, useEffect } from 'react';
import { PeriodoVacaciones, Empleado, TipoLicencia, BalanceEmpleado } from '../tipos/vacaciones';
import * as periodoService from '../services/periodoService';
import './FormularioPeriodo.css';

interface FormularioPeriodoProps {
  periodo?: PeriodoVacaciones | null;
  empleados: Empleado[];
  tiposLicencia: TipoLicencia[];
  balances: BalanceEmpleado[];
  periodos: PeriodoVacaciones[];  // Todos los periodos existentes para validar solapamiento
  empleadoPreseleccionado?: number;
  onGuardar: () => void;
  onCancelar: () => void;
}

function calcularDiasHabiles(inicio: string, fin: string): number {
  if (!inicio || !fin) return 0;
  let dias = 0;
  const fechaInicio = new Date(inicio + 'T00:00:00');
  const fechaFin = new Date(fin + 'T00:00:00');
  if (fechaFin < fechaInicio) return 0;
  for (let d = new Date(fechaInicio); d <= fechaFin; d.setDate(d.getDate() + 1)) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) dias++;
  }
  return dias;
}

const FormularioPeriodo: React.FC<FormularioPeriodoProps> = ({
  periodo,
  empleados,
  tiposLicencia,
  balances,
  periodos,
  empleadoPreseleccionado,
  onGuardar,
  onCancelar,
}) => {
  const esEdicion = !!periodo;

  const [empleadoId, setEmpleadoId] = useState<number>(0);
  const [tipoLicenciaId, setTipoLicenciaId] = useState<number>(0);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [errorServidor, setErrorServidor] = useState<string | null>(null);
  const [erroresValidacion, setErroresValidacion] = useState<Record<string, string>>({});

  useEffect(() => {
    if (periodo) {
      setEmpleadoId(periodo.idEmpleado);
      setFechaInicio(periodo.fechaInicio.split('T')[0]);
      setFechaFin(periodo.fechaFin.split('T')[0]);
      setDescripcion(periodo.descripcion || '');
      // Buscar el tipoLicenciaId por nombre
      const tipo = tiposLicencia.find(t => t.nombre === periodo.tipo);
      setTipoLicenciaId(tipo?.id || 0);
    } else if (empleadoPreseleccionado) {
      setEmpleadoId(empleadoPreseleccionado);
    }
  }, [periodo, tiposLicencia, empleadoPreseleccionado]);

  // Detectar si el tipo seleccionado es "Día de Beneficio" (solo 1 día por carga)
  const tipoSeleccionado = tiposLicencia.find(t => t.id === tipoLicenciaId);
  const esDiaBeneficio = tipoSeleccionado?.codigo === 'DIA_BEN';

  // Cuando se selecciona "Día de Beneficio", forzar fecha fin = fecha inicio (solo 1 día)
  useEffect(() => {
    if (esDiaBeneficio && fechaInicio) {
      setFechaFin(fechaInicio);
    }
  }, [esDiaBeneficio, fechaInicio]);

  // Validación en tiempo real: fecha fin < fecha inicio
  const fechasInvalidas = fechaInicio && fechaFin && new Date(fechaFin + 'T00:00:00') < new Date(fechaInicio + 'T00:00:00');

  // Validación: fecha inicio no puede ser en el pasado (solo para nuevos periodos)
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const fechaInicioEnPasado = !esEdicion && fechaInicio && new Date(fechaInicio + 'T00:00:00') < hoy;

  // Validación: fecha inicio no puede ser fin de semana
  const fechaInicioObj = fechaInicio ? new Date(fechaInicio + 'T00:00:00') : null;
  const fechaFinObj = fechaFin ? new Date(fechaFin + 'T00:00:00') : null;
  const inicioEsFinDeSemana = fechaInicioObj && (fechaInicioObj.getDay() === 0 || fechaInicioObj.getDay() === 6);
  const finEsFinDeSemana = fechaFinObj && (fechaFinObj.getDay() === 0 || fechaFinObj.getDay() === 6);

  const diasHabiles = fechasInvalidas ? 0 : calcularDiasHabiles(fechaInicio, fechaFin);

  // Balance del empleado seleccionado
  const empleadoIdActivo = esEdicion ? (periodo?.idEmpleado || 0) : empleadoId;
  const balanceEmpleado = balances.find(b => b.idEmpleado === empleadoIdActivo);
  const diasRestantes = balanceEmpleado ? balanceEmpleado.diasRestantes : 0;
  const diasTotales = balanceEmpleado ? balanceEmpleado.diasTotales : 0;
  const diasUsados = balanceEmpleado ? balanceEmpleado.diasUsados : 0;

  // En modo edicion, los dias del periodo editado se "devuelven" al balance
  const diasDisponiblesParaEdicion = esEdicion
    ? diasRestantes + (periodo?.dias || 0)
    : diasRestantes;

  // Validación de solapamiento con periodos existentes del mismo empleado
  const periodoSolapado = (() => {
    if (!fechaInicio || !fechaFin || !empleadoIdActivo || fechasInvalidas) return null;
    const inicio = new Date(fechaInicio + 'T00:00:00');
    const fin = new Date(fechaFin + 'T00:00:00');

    return periodos.find(p => {
      // Solo comparar periodos del mismo empleado
      if (p.idEmpleado !== empleadoIdActivo) return false;
      // En edición, excluir el periodo que se está editando
      if (esEdicion && periodo && p.id === periodo.id) return false;

      const pInicio = new Date(p.fechaInicio.split('T')[0] + 'T00:00:00');
      const pFin = new Date(p.fechaFin.split('T')[0] + 'T00:00:00');

      // Hay solapamiento si NO es cierto que uno termina antes de que empiece el otro
      return !(fin < pInicio || inicio > pFin);
    }) || null;
  })();

  const haySolapamiento = !!periodoSolapado;

  // Solo "Vacaciones" (VAC) descuenta del balance. Los demás tipos son ilimitados.
  const esVacaciones = tipoSeleccionado?.codigo === 'VAC';
  const excedeDiasDisponibles = esVacaciones && balanceEmpleado != null && diasHabiles > 0 && diasHabiles > diasDisponiblesParaEdicion;

  // Color del indicador de dias restantes
  const obtenerColorDiasRestantes = () => {
    if (diasDisponiblesParaEdicion <= 0) return 'balance-rojo';
    if (diasDisponiblesParaEdicion <= 5) return 'balance-amarillo';
    return 'balance-verde';
  };

  const validarFormulario = (): boolean => {
    const errores: Record<string, string> = {};

    if (!esEdicion && (!empleadoId || empleadoId === 0)) {
      errores.empleadoId = 'Debe seleccionar un empleado';
    }

    if (!tipoLicenciaId || tipoLicenciaId === 0) {
      errores.tipoLicenciaId = 'Debe seleccionar un tipo de licencia';
    }

    if (!fechaInicio) {
      errores.fechaInicio = 'La fecha de inicio es requerida';
    }

    if (!fechaFin) {
      errores.fechaFin = 'La fecha de fin es requerida';
    }

    if (fechaInicio && fechaFin && new Date(fechaFin) < new Date(fechaInicio)) {
      errores.fechaFin = 'La fecha de fin debe ser igual o posterior a la fecha de inicio';
    }

    if (fechaInicioEnPasado) {
      errores.fechaInicio = 'No se pueden cargar licencias con fecha de inicio en el pasado';
    }

    if (inicioEsFinDeSemana) {
      errores.fechaInicio = 'La fecha de inicio no puede ser sábado o domingo';
    }

    if (finEsFinDeSemana) {
      errores.fechaFin = 'La fecha de fin no puede ser sábado o domingo';
    }

    if (esVacaciones && excedeDiasDisponibles) {
      errores.diasDisponibles = `No tiene suficientes dias disponibles. Disponibles: ${diasDisponiblesParaEdicion}, solicitados: ${diasHabiles}`;
    }

    if (haySolapamiento) {
      errores.solapamiento = 'Las fechas se solapan con un periodo existente del empleado';
    }

    setErroresValidacion(errores);
    return Object.keys(errores).length === 0;
  };

  const manejarEnvio = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorServidor(null);

    if (!validarFormulario()) return;

    setGuardando(true);

    try {
      if (esEdicion && periodo) {
        await periodoService.update(periodo.id, {
          tipoLicenciaId,
          fechaInicio,
          fechaFin,
          descripcion: descripcion.trim() || undefined,
        });
      } else {
        await periodoService.create({
          empleadoId,
          tipoLicenciaId,
          fechaInicio,
          fechaFin,
          descripcion: descripcion.trim() || undefined,
        });
      }
      onGuardar();
    } catch (err) {
      const mensaje = err instanceof Error ? err.message : 'Error al guardar el periodo';
      setErrorServidor(mensaje);
    } finally {
      setGuardando(false);
    }
  };

  const manejarClickOverlay = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !guardando) {
      onCancelar();
    }
  };

  const empleadoEdicion = esEdicion
    ? empleados.find(e => e.id === periodo?.idEmpleado)
    : null;

  return (
    <div className="modal-overlay" onClick={manejarClickOverlay}>
      <div className="modal-formulario">
        <div className="modal-formulario-header">
          <h3>
            <i className={`fas ${esEdicion ? 'fa-edit' : 'fa-calendar-plus'}`}></i>
            {esEdicion ? 'Editar Licencia' : 'Nueva Licencia'}
          </h3>
        </div>

        <form className="modal-formulario-body" onSubmit={manejarEnvio}>
          {/* Empleado: select en modo crear, readonly en modo edicion */}
          <div className="campo-formulario">
            <label htmlFor="per-empleado">Empleado *</label>
            {esEdicion ? (
              <div className="campo-readonly">
                {empleadoEdicion?.nombre || periodo?.nombreEmpleado || 'Empleado no encontrado'}
              </div>
            ) : (
              <>
                <select
                  id="per-empleado"
                  value={empleadoId}
                  onChange={(e) => setEmpleadoId(Number(e.target.value))}
                  disabled={guardando}
                >
                  <option value={0}>Seleccionar empleado...</option>
                  {empleados
                    .filter(e => e.estaActivo)
                    .map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                    ))}
                </select>
                {erroresValidacion.empleadoId && (
                  <span className="error-campo">{erroresValidacion.empleadoId}</span>
                )}
              </>
            )}
          </div>

          {/* Panel informativo de balance - solo para Vacaciones (VAC) */}
          {empleadoIdActivo > 0 && balanceEmpleado && (esVacaciones || !tipoSeleccionado) && (
            <div className={`panel-balance-info ${obtenerColorDiasRestantes()}`}>
              <div className="balance-info-titulo">
                <i className="fas fa-info-circle"></i>
                Balance de dias
              </div>
              <div className="balance-info-detalle">
                <div className="balance-item">
                  <span className="balance-label">Dias totales:</span>
                  <span className="balance-valor">{diasTotales}</span>
                </div>
                <div className="balance-item">
                  <span className="balance-label">Dias usados:</span>
                  <span className="balance-valor">{diasUsados}</span>
                </div>
                <div className="balance-item balance-item-restantes">
                  <span className="balance-label">Dias restantes:</span>
                  <span className="balance-valor balance-valor-restantes">{diasDisponiblesParaEdicion}</span>
                </div>
              </div>
              {esEdicion && periodo && periodo.dias > 0 && (
                <div className="balance-nota-edicion">
                  <i className="fas fa-info-circle"></i>
                  Se incluyen los {periodo.dias} dia(s) del periodo actual que se estan editando.
                </div>
              )}
            </div>
          )}

          {empleadoIdActivo > 0 && !balanceEmpleado && (esVacaciones || !tipoSeleccionado) && (
            <div className="panel-balance-info balance-rojo">
              <div className="balance-info-titulo">
                <i className="fas fa-exclamation-triangle"></i>
                No se encontro balance para este empleado en el anio actual.
              </div>
            </div>
          )}

          <div className="campo-formulario">
            <label htmlFor="per-tipo">Tipo de Licencia *</label>
            <select
              id="per-tipo"
              value={tipoLicenciaId}
              onChange={(e) => setTipoLicenciaId(Number(e.target.value))}
              disabled={guardando}
            >
              <option value={0}>Seleccionar tipo...</option>
              {tiposLicencia.map(tipo => (
                <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>
              ))}
            </select>
            {erroresValidacion.tipoLicenciaId && (
              <span className="error-campo">{erroresValidacion.tipoLicenciaId}</span>
            )}
          </div>

          <div className="campos-fila">
            <div className="campo-formulario">
              <label htmlFor="per-fecha-inicio">Fecha Inicio *</label>
              <input
                id="per-fecha-inicio"
                type="date"
                value={fechaInicio}
                onChange={(e) => {
                  setFechaInicio(e.target.value);
                  // Si es Día de Beneficio, sincronizar fecha fin automáticamente
                  if (esDiaBeneficio) {
                    setFechaFin(e.target.value);
                  }
                }}
                disabled={guardando}
              />
              {erroresValidacion.fechaInicio && (
                <span className="error-campo">{erroresValidacion.fechaInicio}</span>
              )}
            </div>

            <div className="campo-formulario">
              <label htmlFor="per-fecha-fin">
                Fecha Fin *
                {esDiaBeneficio && <span className="nota-campo"> (mismo día)</span>}
              </label>
              <input
                id="per-fecha-fin"
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                disabled={guardando || esDiaBeneficio}
              />
              {erroresValidacion.fechaFin && (
                <span className="error-campo">{erroresValidacion.fechaFin}</span>
              )}
            </div>
          </div>

          {/* Mensaje de error en tiempo real si fecha fin < fecha inicio */}
          {fechasInvalidas && (
            <div className="advertencia-dias-excedidos">
              <i className="fas fa-exclamation-triangle"></i>
              La fecha de fin no puede ser anterior a la fecha de inicio
            </div>
          )}

          {fechaInicioEnPasado && (
            <div className="advertencia-dias-excedidos">
              <i className="fas fa-exclamation-triangle"></i>
              No se pueden cargar licencias con fecha de inicio en el pasado
            </div>
          )}

          {inicioEsFinDeSemana && (
            <div className="advertencia-dias-excedidos">
              <i className="fas fa-exclamation-triangle"></i>
              La fecha de inicio cae en fin de semana (sábado o domingo)
            </div>
          )}

          {finEsFinDeSemana && !esDiaBeneficio && (
            <div className="advertencia-dias-excedidos">
              <i className="fas fa-exclamation-triangle"></i>
              La fecha de fin cae en fin de semana (sábado o domingo)
            </div>
          )}

          {esDiaBeneficio && (
            <div className="info-dias-habiles">
              <i className="fas fa-info-circle"></i>
              Día de Beneficio: se carga de a 1 día por solicitud
            </div>
          )}

          {!fechasInvalidas && !esDiaBeneficio && fechaInicio && fechaFin && diasHabiles >= 0 && (
            <div className={`info-dias-habiles ${excedeDiasDisponibles ? 'info-dias-excedido' : ''}`}>
              <i className="fas fa-calculator"></i>
              Dias habiles: {diasHabiles}
            </div>
          )}

          {excedeDiasDisponibles && (
            <div className="advertencia-dias-excedidos">
              <i className="fas fa-exclamation-triangle"></i>
              No tiene suficientes dias disponibles. Disponibles: {diasDisponiblesParaEdicion}, solicitados: {diasHabiles}
            </div>
          )}

          {haySolapamiento && periodoSolapado && (
            <div className="advertencia-dias-excedidos">
              <i className="fas fa-exclamation-triangle"></i>
              Las fechas se solapan con un periodo existente: {periodoSolapado.tipo} ({new Date(periodoSolapado.fechaInicio).toLocaleDateString('es-ES')} - {new Date(periodoSolapado.fechaFin).toLocaleDateString('es-ES')})
            </div>
          )}

          <div className="campo-formulario">
            <label htmlFor="per-descripcion">Descripcion (opcional)</label>
            <textarea
              id="per-descripcion"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Descripcion adicional..."
              disabled={guardando}
            />
          </div>

          {errorServidor && (
            <div className="error-servidor">
              <i className="fas fa-exclamation-circle"></i>
              {errorServidor}
            </div>
          )}

          <div className="modal-formulario-footer">
            <button
              type="button"
              className="btn-modal btn-cancelar"
              onClick={onCancelar}
              disabled={guardando}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-modal btn-guardar"
              disabled={guardando || excedeDiasDisponibles || !!fechasInvalidas || haySolapamiento || !!fechaInicioEnPasado || !!inicioEsFinDeSemana || !!finEsFinDeSemana}
            >
              {guardando ? (
                <>
                  <span className="btn-spinner"></span>
                  Guardando...
                </>
              ) : (
                'Guardar'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormularioPeriodo;
